'use client';

import React from 'react';
import { CategorisedStaticBenefits } from '@type/job-add';
import { SimpleEmployerProfileList } from '@components/templates/job-add/edit/functions/jobCompanyNameFunctions';
import { useTranslation } from 'next-i18next';
import { Button, TailwindSpinner } from '@components/common';
import { useEditJobForm } from '@components/templates/job-add/edit/hooks/useEditJobForm';
import { showJobDetailModal } from '@components/templates/job-add/edit/modals/JobPreviewModal';
import { getJobPreviewData } from '@components/templates/job-add/edit/functions/jobPreviewFunctions';
import { dataTestId } from '@services/data-test-id';
import { getSelectedActivation } from '@components/templates/job-add/edit/functions/activationFunctions';

import {
  JobActivationServices,
  JobAddressesGroup,
  JobBenefitsList,
  JobCareerLevels,
  JobCompanyName,
  JobDescription,
  JobDomain,
  JobEmploymentTypes,
  JobInterviewQuestions,
  JobOwnApplyUrl,
  JobRequirementQuestions,
  JobSalary,
  JobSpokenLanguages,
  JobSubscribers,
  JobTitle,
  JobVacancies,
  JobWorkSchedule,
} from '@components/templates/job-add/edit/form';

/** Props  */
interface JobEditFormProps {
  locale: Locale,
  isAuth: boolean,
  jobSlug?: string,
  googleApiKey: string,
  hasEmployerProfile?: boolean,
  apiJobTemplate?: ApiSchema<'JobModel2'>,
  apiJobActivationServices?: ApiSchema<'ActivationServices'>,
  apiEmployerProfileList?: SimpleEmployerProfileList[],
  apiJobSubscribers?: ApiSchema<'SubscriberRead'>[],
  apiCareerLevels?: ApiSchema<'OptionItem'>[],
  apiEmploymentTypes?: ApiSchema<'OptionItem'>[],
  apiSpokenLanguages?: ApiSchema<'OptionItem'>[],
  apiSpokenLanguageLevels?: ApiSchema<'OptionItem'>[],
  apiCompanyStatus?: ApiSchema<'AccountStatus'>,
  categorisedStaticBenefits?: CategorisedStaticBenefits[],
}

/**
 * @description The job add/edit form component.
 * - `locale` - The current app locale.
 * - `isAuth` - Whether the user is authenticated.
 * - `jobSlug` - The current job slug (if any).
 * - `googleApiKey` - The Google API key.
 * - `hasEmployerProfile` - Whether the user has an employer profile.
 * - `apiJobTemplate` - The job template data.
 * - `apiJobActivationServices` - The job activation services data.
 * - `apiEmployerProfileList` - The employer profile list.
 * - `apiJobSubscribers` - The job subscribers list.
 * - `apiCareerLevels` - The career levels list.
 * - `apiEmploymentTypes` - The employment types list.
 * - `apiSpokenLanguages` - The spoken languages list.
 * - `apiSpokenLanguageLevels` - The spoken language levels list.
 * - `categorisedStaticBenefits` - The categorised benefits list.
 * - `apiCompanyStatus` - The company status data.
 */
export const JobEditForm = (props: JobEditFormProps) => {
  // Destructure props
  const {
    locale,
    isAuth,
    jobSlug,
    googleApiKey,
    hasEmployerProfile,
    apiJobTemplate,
    apiJobActivationServices,
    apiEmployerProfileList,
    apiJobSubscribers,
    apiCareerLevels,
    apiEmploymentTypes,
    apiSpokenLanguages,
    apiSpokenLanguageLevels,
    categorisedStaticBenefits,
  } = props;

  // Translate
  const { t } = useTranslation('common');

  // Edit job form hook
  const {
    // Job template elements
    setCurrentJobSlug,
    jobTemplate,
    isJobTemplateLoading,
    optimisticJobTemplate,
    // Job description elements
    jobDescriptionRef,
    jobDescriptionImperativeRef,
    // Job languages elements
    setSelectedLanguages,
    // Job interview questions elements
    setInterviewQuestionList,
    // Job requirement questions elements
    setRequirementQuestionList,
    // BJ form elements
    jobFormRef,
    record,
    formValues,
    setFormValues,
    getFieldValue,
    updateFormElements,
    submitForm,
    isSubmitting,
    saveJobOnly,
    getJobFormData,
    // Form mirror data
    mirrorData,
    setMirrorData,
    // Show/hide the job form
    showJobForm,
    showMoreOptions,
    setShowMoreOptions,
  } = useEditJobForm({
    locale,
    jobSlug,
    apiJobTemplate,
    isAuth,
  });

  // Common styles
  const labelDescriptionClass = 'font-bold text-ink-medium px-3.5 !mb-2';

  // Compute a robust bundle identifier for the confidentiality check:
  // 1) prefer mirrorData.bundle (live selection), 2) fallback to jobTemplate.bundle (if provided by backend),
  // 3) fallback to the server-provided activation services default selection
  const initialBundleFromServer = getSelectedActivation(
    apiJobActivationServices?.bundles || [],
    !!jobTemplate?.inActivePeriod,
  )?.identifier;
  const effectiveBundleIdentifier = mirrorData?.bundle ?? jobTemplate?.bundle ?? initialBundleFromServer;

  // Page content
  // *************************
  return (
    <form
      noValidate
      ref={jobFormRef}
      className="grid gap-y-4"
      onSubmit={submitForm}
    >

      {/* Job title */}
      <JobTitle
        record={record}
        setCurrentJobSlug={setCurrentJobSlug}
        optimisticJobTemplate={optimisticJobTemplate}
        isTitleEditable={jobTemplate?.isTitleEditable}
        labelDescriptionClass={labelDescriptionClass}
        setMirrorData={setMirrorData}
        apiTitle={apiJobTemplate?.title}
      />

      {/* Initial hidden form */}
      <div
        className={`relative grid gap-y-4${showJobForm || isJobTemplateLoading ? '' : ' hidden'}`}
        // Rerender the form when the job template changes or is reset (JobTitle component)
        key={JSON.stringify(jobTemplate?.title)}
      >
        {/* Loading indicator */}
        {isJobTemplateLoading && (
          <div className="absolute inset-0 z-3 bg-surface/70">
            <div className="flex h-16 items-center justify-center gap-2 bg-surface/80 pb-3 text-ink-medium sm:h-10 sm:pb-2.5">
              <TailwindSpinner className="size-5 text-secondary" />
              {t('global.event.loading')}
            </div>
          </div>
        )}

        {/* Job addresses */}
        <div className="mt-4 w-full scroll-mt-20">
          <JobAddressesGroup
            record={record}
            getFieldsValue={getFieldValue}
            googleApiKey={googleApiKey}
            initialAddresses={jobTemplate?.addresses}
            initialWorkPlacement={jobTemplate?.workPlacement}
            setMirrorData={setMirrorData}
            // mirrorData changes frequently, pass only what's needed to avoid unnecessary re-renders
            reqMirrorData={{
              currentActivationsGroup: mirrorData?.currentActivationsGroup,
            }}
          />
        </div>

        {/* Job description */}
        <JobDescription
          record={record}
          locale={locale}
          imperativeRef={jobDescriptionImperativeRef}
          jobTitle={mirrorData?.title}
          ref={jobDescriptionRef}
          defaultValue={jobTemplate?.description}
          setMirrorData={setMirrorData}
        />

        {/* Job domain info */}
        <JobDomain
          locale={locale}
          record={record}
          setFormValues={setFormValues}
          apiDomain={jobTemplate?.assignedDomain?.domainName}
          apiOccupation={jobTemplate?.assignedDomain?.escoOccupationName}
          apiDomainId={jobTemplate?.domain}
          shouldFetchDomain={isAuth && !isJobTemplateLoading && !jobTemplate?.inActivePeriod && !!mirrorData?.title}
          labelDescriptionClass={labelDescriptionClass}
          setMirrorData={setMirrorData}
          // mirrorData changes frequently, pass only what's needed to avoid unnecessary re-renders
          reqMirrorData={{
            title: mirrorData?.title,
            description: mirrorData?.description,
          }}
        />

        {/* Job required elements */}
        <div className="grid gap-2 md:grid-cols-2 md:gap-x-8 md:gap-y-4">
          {/* Career levels */}
          <JobCareerLevels
            apiCareerLevels={apiCareerLevels}
            record={record}
            formValues={formValues}
            setFormValues={setFormValues}
            initialSelectedValues={jobTemplate?.careerLevels}
            labelDescriptionClass={labelDescriptionClass}
            setMirrorData={setMirrorData}
          />

          {/* Number of vacancies */}
          <JobVacancies
            record={record}
            setFormValues={setFormValues}
            labelDescriptionClass={labelDescriptionClass}
            canAutoChangeVacancies={!jobSlug}
            setMirrorData={setMirrorData}
            // mirrorData changes frequently, pass only what's needed to avoid unnecessary re-renders
            reqMirrorData={{
              addresses: mirrorData?.addresses,
              vacancies: mirrorData?.vacancies,
            }}
          />

          {/* Salary */}
          <JobSalary
            locale={locale}
            record={record}
            setFormValues={setFormValues}
            labelDescriptionClass={labelDescriptionClass}
            apiSalaryMin={jobTemplate?.salaryMin}
            apiSalaryMax={jobTemplate?.salaryMax}
            setMirrorData={setMirrorData}
            hasInitialSalary={!!jobTemplate?.salaryMin}
            hasInitialConfidential={!!jobTemplate?.salaryConfidential}
            // mirrorData changes frequently, pass only what's needed to avoid unnecessary re-renders
            reqMirrorData={{
              title: mirrorData?.title,
              addresses: mirrorData?.addresses,
              description: mirrorData?.description,
              workPlacement: mirrorData?.workPlacement,
              careerLevels: mirrorData?.careerLevels,
              employmentTypes: mirrorData?.employmentTypes,
            }}
          />

          {/* Employer */}
          {isAuth && (
            <JobCompanyName
              hasEmployerProfile={hasEmployerProfile}
              apiProfileList={apiEmployerProfileList}
              record={record}
              formValues={formValues}
              setFormValues={setFormValues}
              initialSelectedValues={jobTemplate?.companyName}
              locale={locale}
              labelDescriptionClass={labelDescriptionClass}
              setMirrorData={setMirrorData}
              bundleIdentifier={effectiveBundleIdentifier}
            />
          )}

          {/* Interview questions list */}
          {isAuth && (
            <div className="mt-4 md:col-span-2">
              <JobInterviewQuestions
                record={record}
                locale={locale}
                setFormValues={setFormValues}
                getFieldValue={getFieldValue}
                updateFormElements={updateFormElements}
                shouldFetchQuestions={isAuth && !isJobTemplateLoading && !jobTemplate?.inActivePeriod && !!mirrorData?.title}
                setMirrorData={setMirrorData}
                // mirrorData changes frequently, pass only what's needed to avoid unnecessary re-renders
                reqMirrorData={{
                  title: mirrorData?.title,
                  description: mirrorData?.description,
                }}
                initialInterviewQuestions={jobTemplate?.interviewQuestions}
                setInterviewQuestionList={setInterviewQuestionList}
              />
            </div>
          )}

          {/* Requirement questions list */}
          {isAuth && (
            <div className="mt-4 md:col-span-2">
              <JobRequirementQuestions
                record={record}
                locale={locale}
                setFormValues={setFormValues}
                getFieldValue={getFieldValue}
                updateFormElements={updateFormElements}
                shouldFetchQuestions={isAuth && !isJobTemplateLoading && !jobTemplate?.inActivePeriod && !!mirrorData?.title}
                setMirrorData={setMirrorData}
                // mirrorData changes frequently, pass only what's needed to avoid unnecessary re-renders
                reqMirrorData={{
                  title: mirrorData?.title,
                  description: mirrorData?.description,
                }}
                initialRequirementQuestions={jobTemplate?.requirementQuestions}
                setRequirementQuestionList={setRequirementQuestionList}
              />
            </div>
          )}

        </div>


        {/* Show more options button */}
        {!showMoreOptions && (
          <div className="-mb-2 mt-4 border-t border-dashed border-input pt-6 text-center md:grid-cols-2">
            <Button
              size="sm"
              color="primary-light"
              styling="soft"
              rounding="full"
              onClick={() => setShowMoreOptions(true)}
              {...dataTestId('jobAddPage.showMoreButton')}
            >
              {t('job.add.more_options')}
            </Button>
          </div>
        )}

        {/* Job optional elements */}
        <div className={`mt-4 grid gap-2 border-t border-dashed border-input pt-4 md:grid-cols-2 md:gap-x-8 md:gap-y-4${showMoreOptions ? '' : ' hidden'}`}>
          {/* Employment types */}
          <JobEmploymentTypes
            apiEmploymentTypes={apiEmploymentTypes}
            record={record}
            formValues={formValues}
            setFormValues={setFormValues}
            initialSelectedValues={jobTemplate?.employmentTypes}
            labelDescriptionClass={labelDescriptionClass}
            setMirrorData={setMirrorData}
          />

          {/* Own apply URL */}
          <JobOwnApplyUrl
            record={record}
            label={t('job.add.label.own-apply.short')}
            labelHelper={t('job.add.label.own-apply.hint')}
            labelDescriptionClass={labelDescriptionClass}
            setMirrorData={setMirrorData}
          />

          {/* Spoken languages */}
          <div className="md:col-span-2">
            <JobSpokenLanguages
              record={record}
              initialSelectedValues={jobTemplate?.spokenLanguages}
              apiSpokenLanguages={apiSpokenLanguages}
              apiSpokenLanguageLevels={apiSpokenLanguageLevels}
              spokenLanguagesSetter={setSelectedLanguages}
              labelDescriptionClass={labelDescriptionClass}
            />
          </div>

          {/* Employer benefits */}
          {isAuth && (
            <div className="md:col-span-2">
              <JobBenefitsList
                benefits={categorisedStaticBenefits}
                record={record}
                setFormValues={setFormValues}
                getFieldValue={getFieldValue}
                jobTemplate={jobTemplate}
                apiEmployerProfileList={apiEmployerProfileList}
                setMirrorData={setMirrorData}
                // mirrorData changes frequently, pass only what's needed to avoid unnecessary re-renders
                reqMirrorData={{
                  companyName: mirrorData?.companyName,
                }}
              />
            </div>
          )}

          {/* Work schedule */}
          <JobWorkSchedule
            record={record}
            label={t('job.add.label.work_schedule')}
            placeholder={t('job.add.label.work_schedule.placeholder')}
            labelDescriptionClass={labelDescriptionClass}
          />

          {/* Subscribers - allow access to */}
          {isAuth && (
            <JobSubscribers
              apiJobSubscribers={apiJobSubscribers}
              record={record}
              formValues={formValues}
              setFormValues={setFormValues}
              initialSelectedValues={jobTemplate?.subscribers}
              labelDescriptionClass={labelDescriptionClass}
              setMirrorData={setMirrorData}
            />
          )}

        </div>

        {/* Activation services */}
        <div className="mt-4 border-t border-dashed border-input pt-2 text-sm">
          <JobActivationServices
            currentPackageName={jobTemplate?.currentActivationPackageLabel}
            locale={locale}
            record={record}
            setFormValues={setFormValues}
            jobSlug={jobSlug}
            inActivePeriod={!!jobSlug && jobTemplate?.inActivePeriod}
            googleApiKey={googleApiKey}
            setMirrorData={setMirrorData}
            apiJobActivationServices={apiJobActivationServices}
            initialInterviewAddress={jobTemplate?.interviewAddress}
            initialInterviewDateTime={jobTemplate?.interviewDateTime}
            // mirrorData changes frequently, pass only what's needed to avoid unnecessary re-renders
            reqMirrorData={{
              title: mirrorData?.title,
              domain: mirrorData?.domain,
              withoutCharge: mirrorData?.withoutCharge,
              addresses: mirrorData?.addresses,
              salaryConfidential: mirrorData?.salaryConfidential,
              repostingNumber: mirrorData?.repostingNumber,
              vacancies: mirrorData?.vacancies,
              ownApplyUrl: mirrorData?.ownApplyUrl,
              workPlacement: mirrorData?.workPlacement,
              salaryMin: mirrorData?.salaryMin,
              salaryMax: mirrorData?.salaryMax,
            }}
          />
        </div>

        {/* Action buttons */}
        {showJobForm && (
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-col gap-3 sm:flex-row">
              {/* Save button */}
              <Button
                id="save-job-button"
                type="button"
                styling="soft"
                color="primary-light"
                rounding="full"
                className="gap-1.5 !px-8"
                isLoading={saveJobOnly && isSubmitting}
                // Disable save button if submit in progress or if the job is in the inactive period and price is greater than 0
                disabled={(saveJobOnly && isSubmitting) || (!!jobSlug && jobTemplate?.inActivePeriod && (mirrorData?.pricingData?.credits || 0) > 0)}
                onClick={() => {
                  submitForm(jobFormRef, false);
                }}
                {...dataTestId('jobAddPage.jobActions.saveJobButton')}
              >
                {t('global.action.save')}
              </Button>

              {/* Preview job in modal; add it when we have an endpoint */}
              <Button
                type="button"
                styling="outline"
                color="light"
                rounding="full"
                className="gap-1.5 !px-8"
                onClick={() => showJobDetailModal({
                  locale,
                  jobData: getJobPreviewData({
                    getJobFormData,
                    apiCareerLevels,
                    apiEmploymentTypes,
                    apiSpokenLanguages,
                    apiSpokenLanguageLevels,
                    categorisedStaticBenefits,
                    salaryPeriodTrans: t('company.offer.aim.price.period'),
                  }),
                })}
                {...dataTestId('jobAddPage.jobActions.previewJobButton')}
              >
                {t('job.preview.link')}
              </Button>
            </div>

            {/* Submit button */}
            <Button
              id="activate-job-button"
              type="submit"
              color="secondary"
              rounding="full"
              disabled={(!saveJobOnly && isSubmitting) || (!!jobSlug && jobTemplate?.inActivePeriod)}
              className={`${!!jobSlug && jobTemplate?.inActivePeriod ? 'hidden' : ''} gap-1.5 !px-8`}
              isLoading={!saveJobOnly && isSubmitting}
              {...dataTestId('jobAddPage.jobActions.activateJobButton')}
            >
              {t('global.action.activate')}
            </Button>
          </div>
        )}
      </div>
    </form>
  );
};
